import React from "react";

import { Badge, Drawer, Flex, Grid, Tabs, Typography } from "antd";

import { Robot2Line } from "@/components/icon/RobotIcon";

import { AgentSummary } from "./api";
import { nombreDelAsistente, opcionesParaCopiar } from "./asistentes";
import { ConfiguracionTab } from "./ConfiguracionTab";
import { ConocimientoTab } from "./ConocimientoTab";
import { ConsumoTab } from "./ConsumoTab";
import { ProbarTab } from "./ProbarTab";
import { TareasTab } from "./TareasTab";

const { Text } = Typography;

interface Props {
  storeCode: string;
  /** El asistente abierto; sin él, el drawer está cerrado. */
  asistente: AgentSummary | null;
  /** Todos los del store, para «Copiar conocimiento de…». */
  asistentes: AgentSummary[];
  editable: boolean;
  onClose: () => void;
  /** Después de guardar algo que se ve en la lista: encendido, modelo, key, versión. */
  onCambio?: () => void;
}

/**
 * Un asistente del store (tarea 5.11, D62): su configuración, su conocimiento, Probar, su consumo y
 * sus tareas programadas (5.12, D65). Se abre desde `Stores → Asistente IA` y desde la fila del
 * operador en Operadores del Store. Probar crea una prueba, que pide Admin como guardar: un Viewer
 * no la ve.
 */
export const AsistenteDrawer: React.FC<Props> = ({
  storeCode,
  asistente,
  asistentes,
  editable,
  onClose,
  onCambio,
}) => {
  const screens = Grid.useBreakpoint();
  const userId = asistente?.userId ?? 0;

  return (
    <Drawer
      open={!!asistente}
      onClose={onClose}
      width={screens.md ? 760 : "100%"}
      destroyOnClose
      title={
        asistente && (
          <Flex align="center" gap={8}>
            <Robot2Line style={{ fontSize: 20, flexShrink: 0 }} />
            <Flex vertical style={{ minWidth: 0 }}>
              <Text strong ellipsis>
                {nombreDelAsistente(asistente)}
              </Text>
              {asistente.email && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, fontWeight: "normal" }}
                  ellipsis
                >
                  {asistente.email}
                </Text>
              )}
            </Flex>
          </Flex>
        )
      }
      extra={
        asistente && (
          <Badge
            status={asistente.enabled ? "success" : "default"}
            text={asistente.enabled ? "Encendido" : "Apagado"}
          />
        )
      }
    >
      {asistente && (
        <Tabs
          key={userId}
          items={[
            {
              key: "config",
              label: "Configuración",
              children: (
                <ConfiguracionTab
                  storeCode={storeCode}
                  userId={userId}
                  editable={editable}
                  onGuardado={onCambio}
                />
              ),
            },
            {
              key: "conocimiento",
              label: "Conocimiento",
              children: (
                <ConocimientoTab
                  storeCode={storeCode}
                  userId={userId}
                  editable={editable}
                  otros={opcionesParaCopiar(asistentes, userId)}
                  onGuardado={onCambio}
                />
              ),
            },
            ...(editable
              ? [
                  {
                    key: "probar",
                    label: "Probar",
                    children: (
                      <ProbarTab storeCode={storeCode} userId={userId} />
                    ),
                  },
                ]
              : []),
            {
              key: "consumo",
              label: "Consumo",
              children: (
                <ConsumoTab
                  storeCode={storeCode}
                  userId={userId}
                  editable={editable}
                />
              ),
            },
            {
              key: "tareas",
              label: "Tareas",
              children: <TareasTab userId={userId} />,
            },
          ]}
        />
      )}
    </Drawer>
  );
};
